import { motion } from "framer-motion";
import { Check, X, Star, Sparkles } from "lucide-react";

const PricingSection = () => {
  const plans = [
    {
      name: "Simulator",
      badge: "FREE FOREVER",
      price: "$0",
      period: "/mo",
      features: [
        { text: "3 simulated portfolios", included: true },
        { text: "Real-time data (15-min delay)", included: true },
        { text: "Basic AI strategy scoring", included: true },
        { text: "Live trading", included: false },
        { text: "API access", included: false },
      ],
      cta: "Start Free",
      highlight: false,
    },
    {
      name: "Pro",
      badge: "MOST POPULAR",
      originalPrice: "$97",
      price: "$47",
      period: "/mo",
      features: [
        { text: "Unlimited portfolios", included: true },
        { text: "Real-time data (no delay)", included: true },
        { text: "Advanced AI insights", included: true },
        { text: "Rug pull alerts", included: true },
        { text: "5 live strategies", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Join Waitlist",
      highlight: true,
    },
    {
      name: "Founding Member",
      badge: "LIMITED — 100 SPOTS",
      originalPrice: "$197",
      price: "$77",
      period: "/mo",
      features: [
        { text: "Everything in Pro", included: true },
        { text: "Unlimited live strategies", included: true },
        { text: "API access + webhooks", included: true },
        { text: "Custom risk settings", included: true },
        { text: "Monthly 1-on-1 calls", included: true },
        { text: "Team dashboards", included: true },
      ],
      cta: "Become a Founder",
      highlight: false,
      premium: true,
    },
  ];

  return (
    <section
      id="pricing"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="pricing-section"
    >
      {/* Background */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1771875802948-0d0f3424fe6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMHB1cnBsZSUyMHRlY2glMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzU3ODA5NTh8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            EARLY ACCESS PRICING
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 md:mb-4">
            Get In Before
            <br />
            <span className="gradient-text">We Launch</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg">
            First 500 users lock in lifetime pricing. Cancel anytime.
          </p>
        </motion.div>

        {/* Pricing cards - Responsive grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`relative p-6 md:p-8 rounded-2xl md:rounded-3xl ${
                plan.premium
                  ? "animated-border"
                  : plan.highlight
                  ? "glass border-2 border-[#00D4FF]/50"
                  : "glass"
              } ice-hover ${plan.highlight ? "md:-mt-4 md:mb-4" : ""}`}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-mono mb-4 md:mb-6 ${
                  plan.highlight
                    ? "bg-[#00D4FF]/20 text-[#00D4FF]"
                    : plan.premium
                    ? "bg-[#8B5CF6]/20 text-[#8B5CF6]"
                    : "bg-white/10 text-[#A1A1AA]"
                }`}
              >
                {plan.highlight && <Star size={10} className="md:w-3 md:h-3" />}
                {plan.premium && <Sparkles size={10} className="md:w-3 md:h-3" />}
                {plan.badge}
              </div>

              {/* Plan name */}
              <h3 className="font-['Unbounded'] text-xl md:text-2xl font-bold text-white mb-2">
                {plan.name}
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-4 md:mb-6">
                {plan.originalPrice && (
                  <span className="text-[#A1A1AA] line-through text-base md:text-lg">
                    {plan.originalPrice}
                  </span>
                )}
                <span className="font-['Unbounded'] text-3xl md:text-4xl font-black gradient-text">
                  {plan.price}
                </span>
                <span className="text-[#A1A1AA] text-sm md:text-base">{plan.period}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2.5 md:gap-3 text-sm md:text-base"
                  >
                    {feature.included ? (
                      <div className="w-5 h-5 rounded-full bg-[#00D4FF]/20 flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-[#00D4FF]" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                        <X size={12} className="text-[#A1A1AA]/50" />
                      </div>
                    )}
                    <span
                      className={
                        feature.included ? "text-white" : "text-[#A1A1AA]/50"
                      }
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href="#cta"
                className={`block w-full py-3.5 md:py-4 rounded-full text-center font-bold text-sm md:text-base transition-all duration-300 touch-manipulation active:scale-95 ${
                  plan.highlight
                    ? "bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white hover:shadow-[0_0_30px_rgba(0,212,255,0.5)]"
                    : plan.premium
                    ? "bg-[#8B5CF6]/20 text-white border border-[#8B5CF6]/50 hover:bg-[#8B5CF6]/30"
                    : "bg-white/10 text-white border border-white/20 hover:bg-white/20"
                }`}
              >
                {plan.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Trust badges - Responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap justify-center gap-4 md:gap-6 mt-8 md:mt-12 text-xs md:text-sm text-[#A1A1AA]"
        >
          <span className="flex items-center gap-2">
            <Check size={14} className="text-[#10B981] md:w-4 md:h-4" />
            Cancel anytime
          </span>
          <span className="flex items-center gap-2">
            <Check size={14} className="text-[#10B981] md:w-4 md:h-4" />
            No credit card needed
          </span>
          <span className="flex items-center gap-2">
            <Check size={14} className="text-[#10B981] md:w-4 md:h-4" />
            30-day money-back
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
